import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch, Action } from 'redux';
import { AppStates } from '../../reducers/app';
import ToolIconButton from './ToolIconButton';
import * as ViewAction from '../../actions/ViewAction';
import { UIModeState } from '../../reducers/uimode';
import { setRegionZoomMode, ToolbarDirection } from '../../actions/UIModeAction';
import { mapStateToZoomable, getScale, getInitialScale } from '../../selectors/zoomingSelector';
import { UIEvent, getPageX, getPageY } from '../../uievent';
import { tabFocusSelf } from '../../reducers/pages';

interface ZoomSliderProps {
  readonly vertical: boolean;
  readonly zoomingEnabled: boolean;
  readonly zoomInEnabled: boolean;
  readonly zoomOutEnabled: boolean;
  readonly scale: number;
  readonly baseScale: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onScale: (zoom: number) => void;
}

interface ZoomSliderState {
  readonly dragging: boolean;
  readonly mousePrevX: number | null;
  readonly mousePrevY: number | null;
}

const maxZoomRatio = 6;
const sliderLength = 110;

export class ZoomSlider extends React.Component<ZoomSliderProps, ZoomSliderState> {
  private grip: HTMLElement | null = null;

  public constructor(props: ZoomSliderProps) {
    super(props);
    this.state = {
      dragging: false,
      mousePrevX: null,
      mousePrevY: null,
    };
  }

  private readonly handlePointerDown = (ev: UIEvent<HTMLElement>): void => {
    if (!this.props.zoomingEnabled) {
      return;
    }

    this.setState({
      dragging: true,
      mousePrevX: getPageX(ev),
      mousePrevY: getPageY(ev),
    });
    this.startMouseCapture();
  }

  // FIXME : Resizableからコピペが多い
  private readonly handlePointerMove = (ev: MouseEvent | TouchEvent) => {
    if (!this.state.dragging) {
      return;
    }

    // iOS Safariにおいて、getBoundingClientRect()の座標がバウンスしたかのような座標になるのを防止
    ev.preventDefault();

    const pageX = ev instanceof MouseEvent ? ev.pageX : ev.changedTouches[0].pageX;
    const pageY = ev instanceof MouseEvent ? ev.pageY : ev.changedTouches[0].pageY;

    let dx = pageX - this.state.mousePrevX!;
    let dy = pageY - this.state.mousePrevY!;

    // 最大/最小サイズ制約などで、グリップとマウスカーソルの位置がずれていく場合がある。
    // 自然な挙動にするため、逆方向に戻したときは、元のグリップ位置に到達するまでリサイズしない。
    const { right: gripX, bottom: gripY } = this.grip!.getBoundingClientRect();
    if (dx > 0 && pageX < gripX || dx < 0 && pageX > gripX) {
      dx = 0;
    }
    if (dy > 0 && pageY < gripY || dy < 0 && pageY > gripY) {
      dy = 0;
    }

    const minZoom = this.props.baseScale;
    const maxZoom = this.props.baseScale * maxZoomRatio;
    const dist = (this.props.vertical ? -dy : dx) * (maxZoom / sliderLength);
    const scale = Math.min(Math.max(minZoom, this.props.scale + dist), maxZoom);

    this.setState({
      mousePrevX: pageX,
      mousePrevY: pageY,
    });

    this.props.onScale(scale);
  }

  private readonly handlePointerUp = (_ev: MouseEvent | TouchEvent) => {
    this.stopMouseCapture();

    this.setState({ dragging: false });
  }

  private stopMouseCapture(): void {
    if (this.grip !== null) {
      const { ownerDocument } = this.grip;
      if (ownerDocument === null) {
        return;
      }

      ownerDocument.removeEventListener('mousemove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('mouseup', this.handlePointerUp, true);
      ownerDocument.removeEventListener('touchmove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('touchend', this.handlePointerUp, true);
    }
  }

  private startMouseCapture(): void {
    if (this.grip === null) {
      throw new Error('Illegal state.');
    }

    const { ownerDocument } = this.grip;
    if (ownerDocument === null) {
      return;
    }

    ownerDocument.addEventListener('mousemove', this.handlePointerMove, true);
    ownerDocument.addEventListener('mouseup', this.handlePointerUp, true);
    ownerDocument.addEventListener('touchmove', this.handlePointerMove, true);
    ownerDocument.addEventListener('touchend', this.handlePointerUp, true);
  }

  public render() {
    const { vertical, zoomInEnabled, zoomOutEnabled, onZoomIn, onZoomOut } = this.props;
    const barStyle: React.CSSProperties = {
      background: '#b0d0ef',
      position: 'relative',
    };
    const gripStyle: React.CSSProperties = {
      width: '20px',
      height: '20px',
      border: '1px solid white',
      boxSizing: 'border-box',
      background: '#74ade5',
      position: 'absolute',
      cursor: 'pointer',
    };

    const offset = ((this.props.scale / this.props.baseScale - 1) / maxZoomRatio) * sliderLength;
    if (vertical) {
      barStyle.width = '10px';
      barStyle.height = `${sliderLength}px`;
      gripStyle.bottom = `${offset}px`;
      gripStyle.left = '-5px';
    } else {
      barStyle.width = `${sliderLength}px`;
      barStyle.height = '10px';
      gripStyle.top = '-5px';
      gripStyle.left = `${offset}px`;
    }

    if (!this.props.zoomingEnabled) {
      barStyle.filter = 'brightness(0.5)';
    }

    const zoomIn = (
      <ToolIconButton
        key="zoom-in"
        onClick={onZoomIn}
        iconName="zoom-in"
        disabled={!zoomInEnabled}
        title="拡大"
        ariaLabel="Zoom in" />
    );
    const zoomOut = (
      <ToolIconButton
        key="zoom-out"
        onClick={onZoomOut}
        iconName="zoom-out"
        disabled={!zoomOutEnabled}
        title="縮小"
        ariaLabel="Zoom out" />
    );
    const slider = (
      <div key="zoom-slider" style={barStyle}>
        <div
          style={gripStyle}
          ref={el => this.grip = el}
          onMouseDown={this.handlePointerDown}
          onTouchStart={this.handlePointerDown} />
      </div>
    );

    if (vertical) {
      return [zoomIn, slider, zoomOut];
    }
    return [zoomOut, slider, zoomIn];
  }
}

interface ZoomControlProps {
  readonly activePanelID: string;
  readonly zoomingEnabled: boolean;
  readonly zoomInEnabled: boolean;
  readonly zoomOutEnabled: boolean;
  readonly scale: number;
  readonly baseScale: number;
  readonly uimode: UIModeState;
  readonly dispatch: Dispatch<Action>;
}

const activeButtonStyle: React.CSSProperties = {
  color: '#ff0',
};

class ZoomControl extends React.PureComponent<ZoomControlProps> {
  public render() {
    const {
      activePanelID, zoomingEnabled, zoomInEnabled, zoomOutEnabled, scale, baseScale, dispatch,
    } = this.props;
    const vertical = this.props.uimode.toolbarDirection !== ToolbarDirection.Horizontal;

    return <>
      <ToolIconButton
        onClick={() => setRegionZoomMode(!this.props.uimode.regionZoomMode, dispatch)}
        active={this.props.uimode.regionZoomMode}
        iconName="zoom-region"
        style={this.props.uimode.regionZoomMode ? activeButtonStyle : {}}
        title="スポットズーム"
        ariaLabel="Zoom to specified region" />
      <ToolIconButton
        onClick={() => dispatch(ViewAction.resetZoom(activePanelID))}
        iconName="zoom-reset"
        disabled={!zoomOutEnabled}
        title="拡大縮小をリセット"
        ariaLabel="Reset zoom" />
      <ZoomSlider
        vertical={vertical}
        zoomingEnabled={zoomingEnabled}
        zoomInEnabled={zoomInEnabled}
        zoomOutEnabled={zoomOutEnabled}
        scale={scale}
        baseScale={baseScale}
        onZoomIn={() => { if (zoomInEnabled) dispatch(ViewAction.zoomIn(activePanelID)); }}
        onZoomOut={() => { if (zoomOutEnabled) dispatch(ViewAction.zoomOut(activePanelID)); }}
        onScale={zoom => dispatch(ViewAction.setZoom(activePanelID, zoom))} />
    </>;
  }
}

const mapStateToProps = (appStates: AppStates) => {
  const { pages } = appStates;
  const tab = pages.tabs[pages.activeTab];
  const panelID = tab.focus === tabFocusSelf ? tab.id : tab.focus;
  return {
    ...mapStateToZoomable(appStates, { panelID }),
    scale: getScale(appStates, { panelID }),
    baseScale: getInitialScale(appStates, { panelID }),
    activePanelID: panelID,
    uimode: appStates.uimode,
  };
};

export default connect(mapStateToProps)(ZoomControl);
